/**
 * useUpdateNotification · detecta nuevas versiones del frontend deployadas y
 * notifica al user con un toast accionable "Recargar".
 *
 * Mecanismo:
 *  1. Al montar guarda `currentBuildId` leyendo `<meta name="dendev-build-id">`
 *     del DOM (inyectado por buildVersionPlugin en vite.config.ts).
 *  2. Cada UPDATE_CHECK_INTERVAL_MS hace fetch a `/` (HTML root, no-cache),
 *     parsea el HTML y extrae la meta tag del deploy actual del server.
 *  3. Si el `buildId` remoto difiere del local Y el user no dismisseó esa
 *     versión específica antes → toast persistente con acción "Recargar".
 *  4. Al cerrar el toast manualmente, guardamos `dismissed:<id>` en
 *     localStorage para no insistir con el mismo build (evita el problema
 *     "queda constantemente" que tuvimos antes).
 *  5. Al recargar, el `currentBuildId` será el nuevo y el dismiss queda
 *     irrelevante.
 *
 * **Por qué meta tag en HTML y no /__build.json**: Cloudflare Bot Management
 * en core.dendev.ar bloquea fetches background a paths no-conocidos con un
 * JS challenge que el fetch no puede resolver. El HTML root (/) es la página
 * de entrada y no sufre del mismo bloqueo.
 *
 * No corre en dev de forma útil: la meta tag SÍ existe (transformIndexHtml
 * la inyecta en dev también), pero como el server hot-reloadea el mismo
 * buildId no va a haber mismatch nunca.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Estilo del botón de acción del toast (brand color, no el blanco default de
// sonner). Compartido por las dos fuentes de detección de updates.
const ACTION_BUTTON_CLASS =
  '!bg-[var(--primary)] !text-[var(--primary-foreground)] !rounded-md !px-3 !py-1.5 !text-caption !font-semibold hover:!brightness-110';

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const DISMISSED_PREFIX = 'dendev:update-dismissed:';
const TOAST_ID = 'app-update-available';
const META_NAME = 'dendev-build-id';

function readMetaBuildId(html: string): string | null {
  // Regex tolerante a orden de atributos y comillas. No usamos DOMParser
  // porque parsear HTML completo es lento y solo necesitamos un atributo.
  const match = html.match(
    /<meta[^>]+name=["']dendev-build-id["'][^>]*content=["']([^"']+)["']/i,
  ) ?? html.match(
    /<meta[^>]+content=["']([^"']+)["'][^>]*name=["']dendev-build-id["']/i,
  );
  return match?.[1] ?? null;
}

async function fetchLatestBuildId(): Promise<string | null> {
  try {
    // Fetch al HTML root con cache busting. CF Pages no cachea HTML por default.
    const res = await fetch(`/?cb=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'cache-control': 'no-cache',
        // Avisamos que aceptamos HTML para que algún proxy intermedio no
        // intente devolvernos otro content-type.
        accept: 'text/html',
      },
    });
    if (!res.ok) return null;
    const html = await res.text();
    return readMetaBuildId(html);
  } catch {
    return null; // offline / bloqueo CF / etc.
  }
}

function readCurrentBuildIdFromDom(): string | null {
  if (typeof document === 'undefined') return null;
  const tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${META_NAME}"]`);
  return tag?.content ?? null;
}

function wasDismissed(buildId: string): boolean {
  try {
    return localStorage.getItem(`${DISMISSED_PREFIX}${buildId}`) === '1';
  } catch {
    return false;
  }
}

function markDismissed(buildId: string): void {
  try {
    localStorage.setItem(`${DISMISSED_PREFIX}${buildId}`, '1');
  } catch {
    /* localStorage bloqueado, lo ignoramos */
  }
}

/**
 * Limpia entradas viejas de localStorage de builds que ya no aplican.
 * Mantenemos solo la del build actual y la más reciente notificada.
 */
function cleanupOldDismissed(currentBuildId: string): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DISMISSED_PREFIX) && !key.endsWith(currentBuildId)) {
        toRemove.push(key);
      }
    }
    // Dejamos las 5 más recientes por las dudas (rotación de toasts en sesiones largas).
    toRemove.slice(0, -5).forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export function useUpdateNotification(): void {
  // ── Fuente A · Service Worker (precache de vite-plugin-pwa, modo prompt) ──
  // Cuando hay una versión nueva precacheada, el SW queda "waiting" y
  // `needRefresh` pasa a true. Es la señal más confiable de "deploy nuevo".
  const {
    needRefresh: [swNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      console.error('[sw] register error', error);
    },
  });

  useEffect(() => {
    if (!swNeedRefresh) return;
    // Mismo TOAST_ID que la fuente B → si ambas disparan, se coalescen en uno.
    toast.info('Nueva versión disponible', {
      id: TOAST_ID,
      description: 'Hay cambios nuevos esperando · aplicalos cuando estés listo.',
      duration: Infinity,
      action: {
        label: 'Aplicar ahora',
        // updateServiceWorker(true) hace skipWaiting + recarga con assets nuevos.
        onClick: () => void updateServiceWorker(true),
      },
      classNames: { actionButton: ACTION_BUTTON_CLASS },
    });
  }, [swNeedRefresh, updateServiceWorker]);

  // ── Fuente B · polling de la meta `dendev-build-id` (fallback) ──
  // Sigue activa por si el SW no detecta el update (ej. SW deshabilitado, o
  // browser sin soporte). Coalesce con la fuente A por TOAST_ID.
  // Evita disparar el check múltiples veces si el componente re-monta en strict mode.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    // Preferimos leer la meta tag del DOM (siempre presente en prod) — refleja
    // exactamente el HTML que el navegador descargó. Fallback a __BUILD_ID__
    // global (también presente en dev).
    const currentBuildId =
      readCurrentBuildIdFromDom() ??
      (typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev');
    cleanupOldDismissed(currentBuildId);

    const showUpdateToast = (latestBuildId: string) => {
      if (wasDismissed(latestBuildId)) return;
      toast.info('Nueva versión disponible', {
        id: TOAST_ID,
        description: 'Hay cambios nuevos esperando · aplicalos cuando estés listo.',
        duration: Infinity,
        action: {
          label: 'Aplicar ahora',
          onClick: () => window.location.reload(),
        },
        // Override del action button para que use el brand color (no el
        // estilo default blanco "muy CRUD"). !important porque sonner
        // aplica sus propios estilos via [data-button].
        classNames: { actionButton: ACTION_BUTTON_CLASS },
        onDismiss: () => markDismissed(latestBuildId),
        // sonner permite cancel con label distinto; usamos onAutoClose para safety.
        onAutoClose: () => markDismissed(latestBuildId),
      });
    };

    const check = async () => {
      const latest = await fetchLatestBuildId();
      if (!latest) {
        // Probable cookie cf_bm expirado o challenge en background → no
        // mostramos toast pero logueamos para tener un trail si Denis abre
        // DevTools cuando reporta "no apareció".
        console.debug('[updates] no pude leer la meta del server (cf challenge o offline)');
        return;
      }
      if (latest !== currentBuildId) {
        console.info(`[updates] versión nueva detectada: ${latest} (actual: ${currentBuildId})`);
        showUpdateToast(latest);
      }
    };
    console.debug(`[updates] activo · buildId actual: ${currentBuildId}`);

    // Check inicial diferido un poco para no competir con primer paint.
    const initialTimeout = setTimeout(check, 15_000);
    const interval = setInterval(check, UPDATE_CHECK_INTERVAL_MS);

    // Check oportunista cuando el user vuelve al tab — muy probable que pase tiempo
    // entre que se deploya algo y el user re-foca la app.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
